import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import type { AdminView } from "@/lib/api/types";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { PageHeaderActions } from "@/shared/layout/PageHeaderActions";
import { Button, Card, ErrorState, KeyValues, LoadingState, TableEmpty, Tag } from "@/shared/rd";
import { useAdminsQuery } from "../services/useAdminsQuery";
import { AdminEditor } from "../components/AdminEditor";
import { AVATAR_TINTS, ROLE_META, ROLE_ORDER, initialsOf } from "../lib/roles";

/** Which drawer the route asks for: `admins/new`, `admins/:id`, or none. */
export type AdminsDrawer = { mode: "list" } | { mode: "create" } | { mode: "edit"; adminId: string };

type Props = {
  locale: string;
  drawer?: AdminsDrawer;
};

/**
 * Who can sign in to this admin, as cards, with what each role unlocks.
 * Create and edit open in a drawer over the list; the drawer has its own URL
 * (`admins/new`, `admins/:id`) and closing it navigates back to `admins`.
 * Super admins only — anyone else is sent to the dashboard.
 */
export function AdminsPage({ locale, drawer = { mode: "list" } }: Props) {
  const navigate = useNavigate();
  const t = useTranslations();
  const { data: session, isLoading: isSessionLoading } = useSessionQuery();
  const isSuperAdmin = session?.admin?.roles?.includes(ADMIN_ROLES.SUPER_ADMIN) ?? false;
  const listHref = `/${locale}/admin/admins`;

  useEffect(() => {
    if (!isSessionLoading && !isSuperAdmin) {
      navigate(`/${locale}/admin`, { replace: true });
    }
  }, [isSessionLoading, isSuperAdmin, locale, navigate]);

  const { data: admins, isLoading, error, refetch, isRefetching } = useAdminsQuery();

  const editing = useMemo<AdminView | null>(
    () => (drawer.mode === "edit" ? (admins?.find((item) => item.id === drawer.adminId) ?? null) : null),
    [admins, drawer],
  );

  if (isSessionLoading || !isSuperAdmin || isLoading) {
    return (
      <LoadingState title={t(keys.shell.loadingTitle, { page: t("admin.adminsPage") })} sub={t(keys.shell.loadingSub)} rows={4} />
    );
  }

  if (error || !admins) {
    return (
      <ErrorState
        title={t(keys.shell.errorTitle, { page: t("admin.adminsPage") })}
        sub={t(keys.shell.errorSub)}
        onRetry={() => void refetch()}
        retryLabel={isRefetching ? t(keys.shell.retrying) : t(keys.shell.retry)}
        retrying={isRefetching}
      />
    );
  }

  const target = drawer.mode === "create" ? "new" : editing;

  return (
    <>
      <PageHeaderActions>
        <Button variant="primary" size="sm" icon={Plus} onClick={() => navigate(`${listHref}/new`)}>
          {t(keys.admin.adminCreateAction)}
        </Button>
      </PageHeaderActions>

      {admins.length === 0 ? (
        <div className="rd-card">
          <TableEmpty
            illustration="admin-empty-table"
            title={t("admin.adminsEmptyTitle")}
            actions={
              <Button variant="primary" size="sm" icon={Plus} onClick={() => navigate(`${listHref}/new`)}>
                {t(keys.admin.adminCreateAction)}
              </Button>
            }
          >
            {t("admin.adminsEmptySub")}
          </TableEmpty>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))",
            gap: 12,
          }}
        >
          {admins.map((admin, index) => (
            <button
              key={admin.id}
              type="button"
              className="rd-card"
              onClick={() => navigate(`${listHref}/${admin.id}`)}
              aria-current={drawer.mode === "edit" && drawer.adminId === admin.id ? "true" : undefined}
              style={{
                border: 0,
                padding: "16px 18px",
                display: "flex",
                flexDirection: "column",
                gap: 10,
                textAlign: "start",
                font: "inherit",
                color: "var(--rd-text)",
                cursor: "pointer",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <span
                  aria-hidden="true"
                  style={{
                    width: 36,
                    height: 36,
                    flex: "none",
                    borderRadius: "50%",
                    background: AVATAR_TINTS[index % AVATAR_TINTS.length],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  {initialsOf(admin.name)}
                </span>
                <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.3, minWidth: 0 }}>
                  <span dir="auto" style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {admin.name}
                  </span>
                  <span
                    dir="ltr"
                    style={{
                      fontSize: 12,
                      color: "var(--rd-text-meta)",
                      textAlign: "start",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {admin.email}
                  </span>
                </span>
              </span>
              {admin.roles?.length ? (
                <span style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {admin.roles.map((role) => (
                    <Tag key={role} tone={ROLE_META[role]?.tone ?? "grey"}>
                      {ROLE_META[role] ? t(ROLE_META[role].label) : role}
                    </Tag>
                  ))}
                </span>
              ) : null}
              {admin.phone ? (
                <span className="rd-num" dir="ltr" style={{ fontSize: 13, color: "var(--rd-text-secondary)" }}>
                  {admin.phone}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      )}

      <Card padding="16px 20px" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{t("admin.rolesUnlockTitle")}</h2>
        <div style={{ fontSize: 13 }}>
          <KeyValues
            rows={ROLE_ORDER.map((role) => ({ label: t(ROLE_META[role].label), value: t(ROLE_META[role].unlocks) }))}
          />
        </div>
      </Card>

      <AdminEditor
        target={target}
        notFound={drawer.mode === "edit" && !editing}
        onClose={() => navigate(listHref)}
        onCreated={() => navigate(listHref, { replace: true })}
      />
    </>
  );
}
